import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import JSZip from "jszip";
import { KeyRound, Copy, Check, ShieldCheck, Clock, ArrowRight, Download, Search, Package, Star, ClipboardList, PackageOpen, Loader2 } from "lucide-react";
import { ReviewDialog } from "@/components/reviews/review-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/_authenticated/_account/licenses")({
  head: () => ({
    meta: [
      { title: "License keys — CoderLity" },
      { name: "description", content: "Software license keys delivered to your account." },
    ],
  }),
  component: LicensesPage,
});

type LicenseRow = {
  id: string;
  key_value: string;
  status: string;
  issued_at: string | null;
  expires_at: string | null;
  product_id: string;
  issued_to_order_item_id: string | null;
  products: { name: string; slug: string; cover_url: string | null; metadata: Record<string, unknown> | null } | null;
};

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  issued: "default",
  available: "secondary",
  revoked: "destructive",
  expired: "outline",
};

function LicensesPage() {
  const { user } = useAuth();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "active" | "expired">("all");
  const [reviewFor, setReviewFor] = useState<{ productId: string; name: string } | null>(null);
  const [bundling, setBundling] = useState(false);
  const [selectedVersion, setSelectedVersionState] = useState<Record<string, string>>(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("licenses.selectedVersion");
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });
  const setSelectedVersion: typeof setSelectedVersionState = (updater) => {
    setSelectedVersionState((prev) => {
      const next = typeof updater === "function" ? (updater as (p: Record<string, string>) => Record<string, string>)(prev) : updater;
      try { window.localStorage.setItem("licenses.selectedVersion", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  type SoftwareVersion = {
    version: string;
    download_url: string;
    notes?: string;
    released_at?: string;
    file_name?: string | null;
    file_size?: number | null;
    mime_type?: string | null;
  };
  const productVersions = (row: LicenseRow): SoftwareVersion[] => {
    const md = (row.products?.metadata ?? {}) as Record<string, unknown>;
    const list = Array.isArray(md.versions) ? (md.versions as any[]) : [];
    const valid = list
      .filter((v) => v && typeof v === "object" && typeof v.version === "string" && typeof v.download_url === "string")
      .map((v) => ({
        version: String(v.version),
        download_url: String(v.download_url),
        notes: v.notes ? String(v.notes) : undefined,
        released_at: v.released_at ? String(v.released_at) : undefined,
        file_name: v.file_name ? String(v.file_name) : null,
        file_size: typeof v.file_size === "number" ? v.file_size : null,
        mime_type: v.mime_type ? String(v.mime_type) : null,
      }));
    if (valid.length > 0) return valid;
    // Fallback to legacy single-version fields.
    const legacyUrl = typeof md.download_url === "string" ? md.download_url : "";
    const legacyVer = typeof md.version === "string" ? md.version : "";
    return legacyUrl ? [{ version: legacyVer || "current", download_url: legacyUrl }] : [];
  };

  const fmtBytes = (bytes?: number | null): string | null => {
    if (!bytes || bytes <= 0) return null;
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let n = bytes;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
  };

  const resolveVersion = (row: LicenseRow): SoftwareVersion | null => {
    const list = productVersions(row);
    if (list.length === 0) return null;
    const chosen = selectedVersion[row.id];
    return list.find((v) => v.version === chosen) ?? list[0];
  };

  const downloadUrlFor = (row: LicenseRow): { url: string | null; version: string | null } => {
    const v = resolveVersion(row);
    return v ? { url: v.download_url, version: v.version } : { url: null, version: null };
  };


  const { data, isLoading } = useQuery({
    enabled: !!user,
    queryKey: ["my-licenses", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("license_keys")
        .select(
          "id, key_value, status, issued_at, expires_at, product_id, issued_to_order_item_id, products:product_id(name, slug, cover_url, metadata)"
        )
        .eq("issued_to_user_id", user!.id)
        .order("issued_at", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return data as unknown as LicenseRow[];
    },
  });

  const rows = useMemo(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((k) => {
      const expired = !!(k.expires_at && new Date(k.expires_at) < new Date());
      if (tab === "active" && (expired || k.status === "revoked" || k.status === "expired")) return false;
      if (tab === "expired" && !(expired || k.status === "expired")) return false;
      if (!q) return true;
      return [k.key_value, k.products?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data, search, tab]);

  const copy = async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      toast.success("License key copied");
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const copyAll = async () => {
    if (rows.length === 0) {
      toast.error("No keys to copy");
      return;
    }
    const text = rows
      .map((k) => `${k.products?.name ?? "Software"}: ${k.key_value}`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${rows.length} license key${rows.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Could not copy");
    }
  };

  const download = (row: LicenseRow) => {
    const { url } = downloadUrlFor(row);
    if (!url) {
      toast.error("No download available for this product yet");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const activeDownloadable = useMemo(() => {
    return (data ?? []).filter((k) => {
      const expired = !!(k.expires_at && new Date(k.expires_at) < new Date());
      const list = productVersions(k);
      return !expired && k.status === "issued" && list.length > 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedVersion]);

  const slugify = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "software";

  const extFromUrl = (url: string, contentType?: string | null) => {
    try {
      const path = new URL(url).pathname;
      const m = path.match(/\.([a-z0-9]{1,8})$/i);
      if (m) return m[1].toLowerCase();
    } catch {
      // ignore
    }
    if (contentType?.includes("zip")) return "zip";
    if (contentType?.includes("pdf")) return "pdf";
    if (contentType?.includes("octet-stream")) return "bin";
    return "bin";
  };

  const downloadAll = async () => {
    if (activeDownloadable.length === 0) {
      toast.error("No active downloads available");
      return;
    }
    setBundling(true);
    const tId = toast.loading(`Bundling ${activeDownloadable.length} download${activeDownloadable.length === 1 ? "" : "s"}…`);
    try {
      const zip = new JSZip();
      const manifest: string[] = [
        "CoderLity — license bundle",
        `Generated: ${new Date().toISOString()}`,
        "",
      ];
      const failed: string[] = [];
      const usedNames = new Set<string>();

      for (const k of activeDownloadable) {
        const name = k.products?.name ?? "Software";
        const meta = resolveVersion(k);
        const { url: resolvedUrl, version } = downloadUrlFor(k);
        const url = resolvedUrl!;
        manifest.push(`• ${name}${version ? ` (v${version})` : ""}`);
        manifest.push(`  Key: ${k.key_value}`);
        if (k.issued_at) manifest.push(`  Issued: ${new Date(k.issued_at).toISOString()}`);
        if (k.expires_at) manifest.push(`  Expires: ${new Date(k.expires_at).toISOString()}`);
        if (version) manifest.push(`  Version: ${version}`);
        if (meta?.released_at) manifest.push(`  Released: ${new Date(meta.released_at).toISOString()}`);
        if (meta?.file_name) manifest.push(`  Original filename: ${meta.file_name}`);
        if (meta?.file_size) manifest.push(`  Size: ${fmtBytes(meta.file_size)} (${meta.file_size} bytes)`);
        if (meta?.mime_type) manifest.push(`  Type: ${meta.mime_type}`);
        if (meta?.notes) manifest.push(`  Notes: ${meta.notes.replace(/\s+/g, " ").trim()}`);
        manifest.push(`  Download URL: ${url}`);

        try {
          const res = await fetch(url, { mode: "cors" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const ext = extFromUrl(url, res.headers.get("content-type"));
          let base = slugify(name);
          let filename = `${base}.${ext}`;
          let i = 2;
          while (usedNames.has(filename)) filename = `${base}-${i++}.${ext}`;
          usedNames.add(filename);
          zip.file(filename, blob);
          manifest.push(`  Bundled as: ${filename}`);
          manifest.push(`  Bundled size: ${fmtBytes(blob.size)} (${blob.size} bytes)`);
        } catch (err) {
          failed.push(name);
          manifest.push(`  Bundled as: (failed to fetch — open the Download URL directly)`);
        }
        manifest.push("");
      }

      if (failed.length) {
        manifest.push("Note: Some downloads could not be bundled (likely blocked by CORS).");
        manifest.push("Use the per-product Download button or the Source URLs above to retrieve them.");
      }
      zip.file("LICENSES.txt", manifest.join("\n"));

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `coderlity-licenses-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.dismiss(tId);
      if (failed.length) {
        toast.warning(`Bundled with ${failed.length} skipped`, {
          description: `${failed.join(", ")} couldn't be fetched. See LICENSES.txt inside the zip.`,
        });
      } else {
        toast.success(`Downloaded bundle of ${activeDownloadable.length} item${activeDownloadable.length === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.dismiss(tId);
      toast.error("Could not generate bundle");
    } finally {
      setBundling(false);
    }
  };


  const tabs: { id: typeof tab; label: string }[] = [
    { id: "all", label: `All (${data?.length ?? 0})` },
    { id: "active", label: "Active" },
    { id: "expired", label: "Expired" },
  ];

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My licenses & downloads</h1>
          <p className="text-sm text-muted-foreground">
            View every license issued to your account, copy keys, and download your purchased software.
          </p>
        </div>
        {data && data.length > 0 && (
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by product or key…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        )}
      </div>

      {data && data.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {tabs.map((t) => (
              <Button
                key={t.id}
                variant={tab === t.id ? "default" : "outline"}
                size="sm"
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={downloadAll}
              disabled={activeDownloadable.length === 0 || bundling}
              title={
                activeDownloadable.length === 0
                  ? "No active licenses have a download available"
                  : "Bundle all active downloads as a zip"
              }
            >
              {bundling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PackageOpen className="h-3.5 w-3.5" />}
              {bundling ? "Bundling…" : `Download all${activeDownloadable.length > 0 ? ` (${activeDownloadable.length})` : ""}`}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={copyAll}
              disabled={rows.length === 0}
            >
              <ClipboardList className="h-3.5 w-3.5" />
              Copy all keys{rows.length > 0 ? ` (${rows.length})` : ""}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Loading licenses…</CardContent></Card>
      ) : !data || data.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No license keys yet</CardTitle>
            <CardDescription>
              Buy any software product and your activation key will appear here instantly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="gap-2">
              <Link to="/software">Browse software <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No licenses match your filter.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((k) => {
            const expired = !!(k.expires_at && new Date(k.expires_at) < new Date());
            const effectiveStatus = expired ? "expired" : k.status;
            const versions = productVersions(k);
            const hasAnyDownload = versions.length > 0;
            const currentVersion = selectedVersion[k.id] ?? versions[0]?.version ?? "";
            const currentMeta = resolveVersion(k);
            const sizeLabel = fmtBytes(currentMeta?.file_size);
            const canDownload = hasAnyDownload && !expired && k.status === "issued";
            return (
              <Card key={k.id}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      {k.products?.cover_url ? (
                        <img src={k.products.cover_url} alt="" className="h-12 w-12 shrink-0 rounded-md border border-border object-cover" />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <KeyRound className="h-4 w-4 text-primary" />
                          {k.products?.name ?? "Software license"}
                        </CardTitle>
                        <CardDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
                          {k.issued_at && (
                            <span className="inline-flex items-center gap-1">
                              <ShieldCheck className="h-3.5 w-3.5" /> Issued{" "}
                              {new Date(k.issued_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                            </span>
                          )}
                          {k.expires_at && (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" /> Expires{" "}
                              {new Date(k.expires_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                            </span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant={statusVariant[effectiveStatus] ?? "secondary"} className="capitalize">
                      {effectiveStatus}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 p-2">
                    <code className="flex-1 min-w-0 break-all font-mono text-sm">{k.key_value}</code>
                    <Button size="sm" variant="outline" onClick={() => copy(k.id, k.key_value)} className="gap-1.5">
                      {copiedId === k.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copiedId === k.id ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        className="gap-1.5"
                        disabled={!canDownload}
                        onClick={() => download(k)}
                        title={
                          canDownload
                            ? `Download${currentMeta?.file_name ? ` ${currentMeta.file_name}` : ""}${sizeLabel ? ` (${sizeLabel})` : ""}`
                            : hasAnyDownload ? "License inactive" : "Download not available"
                        }
                      >
                        <Download className="h-3.5 w-3.5" />
                        {canDownload
                          ? `Download${currentVersion ? ` v${currentVersion}` : ""}${sizeLabel ? ` · ${sizeLabel}` : ""}`
                          : hasAnyDownload ? "Unavailable" : "No download"}
                      </Button>
                      {canDownload && currentMeta?.file_name && (
                        <span className="truncate text-xs text-muted-foreground max-w-[220px]" title={currentMeta.file_name}>
                          {currentMeta.file_name}
                        </span>
                      )}
                      {versions.length > 1 && (
                        <Select
                          value={currentVersion}
                          onValueChange={(v) => setSelectedVersion((prev) => ({ ...prev, [k.id]: v }))}
                        >
                          <SelectTrigger className="h-9 w-[160px]">
                            <SelectValue placeholder="Version" />
                          </SelectTrigger>
                          <SelectContent>
                            {versions.map((v, idx) => (
                              <SelectItem key={v.version} value={v.version}>
                                v{v.version}{idx === 0 ? " (latest)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => setReviewFor({ productId: k.product_id, name: k.products?.name ?? "Software" })}
                      >
                        <Star className="h-3.5 w-3.5" /> Review
                      </Button>
                      <Button asChild size="sm" variant="ghost" className="gap-1.5">
                        <Link to="/software">
                          Browse software <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                  {currentMeta && (currentMeta.released_at || currentMeta.notes) && (
                    <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
                      {currentMeta.released_at && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" />
                          Released {new Date(currentMeta.released_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                        </div>
                      )}
                      {currentMeta.notes && (
                        <p className="mt-1 whitespace-pre-wrap text-foreground/90">{currentMeta.notes}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {reviewFor && (
        <ReviewDialog
          open={!!reviewFor}
          onOpenChange={(v) => !v && setReviewFor(null)}
          productId={reviewFor.productId}
          productName={reviewFor.name}
        />
      )}
    </>
  );
}
